<template>
  <main class="admin-page overview-page" v-loading="loading">
    <header class="page-heading">
      <div>
        <div class="page-kicker">OPERATIONS OVERVIEW</div>
        <h1 class="page-title">运营总览</h1>
        <p class="page-lead">从审核待办开始工作，随后查看内容增长与平台运行状态。</p>
      </div>
      <el-button icon="el-icon-refresh" :loading="loading" @click="load">同步最新数据</el-button>
    </header>

    <el-alert v-if="error" :title="error" type="error" show-icon :closable="false" class="page-alert">
      <el-button slot="default" type="text" @click="load">重新连接</el-button>
    </el-alert>

    <section class="command-card">
      <div class="command-copy">
        <span class="command-label">今日工作台</span>
        <strong>{{ totalPending }}</strong>
        <span>项内容等待处理</span>
      </div>
      <div class="command-note">
        <span v-if="totalPending">优先处理机器检测已返回的素材，再审核公开剧情。</span>
        <span v-else>当前审核队列已清空，平台运行平稳。</span>
      </div>
      <el-button type="primary" @click="go(primaryQueue.path)">{{ primaryQueue.action }}</el-button>
    </section>

    <section class="metric-grid" aria-label="平台核心数据">
      <article v-for="item in metricCards" :key="item.label" class="metric-card">
        <div class="metric-top"><span>{{ item.label }}</span><i :class="item.icon" /></div>
        <strong>{{ item.value }}</strong>
        <p>{{ item.note }}</p>
      </article>
    </section>

    <section class="overview-grid">
      <article class="panel trend-panel">
        <div class="panel-head">
          <div><span class="panel-kicker">近 7 日</span><h2>真实业务新增</h2></div>
          <div class="legend"><span><i class="dot users" />新用户</span><span><i class="dot demands" />剧情</span><span><i class="dot media" />素材</span></div>
        </div>
        <div class="chart" role="img" aria-label="近七日用户、剧情和素材新增趋势">
          <div v-for="point in trends" :key="point.day" class="chart-day">
            <div class="bars">
              <i class="bar users" :style="barStyle(point.users)" :title="`用户 ${point.users}`" />
              <i class="bar demands" :style="barStyle(point.demands)" :title="`剧情 ${point.demands}`" />
              <i class="bar media" :style="barStyle(point.materials)" :title="`素材 ${point.materials}`" />
            </div>
            <span>{{ shortDay(point.day) }}</span>
          </div>
        </div>
      </article>

      <aside class="panel queue-panel">
        <div class="panel-head"><div><span class="panel-kicker">REVIEW QUEUE</span><h2>审核队列</h2></div></div>
        <button v-for="item in queueCards" :key="item.key" class="queue-row" @click="go(item.path)">
          <span class="queue-index">{{ item.index }}</span>
          <span class="queue-copy"><strong>{{ item.label }}</strong><small>{{ item.note }}</small></span>
          <b :class="{ empty: !item.value }">{{ item.value }}</b>
          <i class="el-icon-arrow-right" />
        </button>
      </aside>
    </section>

    <section class="overview-grid lower-grid">
      <article class="panel content-panel">
        <div class="panel-head"><div><span class="panel-kicker">CONTENT LIBRARY</span><h2>素材结构</h2></div><el-button type="text" @click="go('/videos')">进入内容审核</el-button></div>
        <div class="content-total"><strong>{{ formatNumber(metrics.materials.total) }}</strong><span>份已进入平台的媒体内容</span></div>
        <div class="content-bars">
          <div v-for="item in contentMix" :key="item.label" class="content-line">
            <div><span>{{ item.label }}</span><b>{{ item.value }}</b></div>
            <div class="progress-track"><i :style="{ width: item.percent + '%', background: item.color }" /></div>
          </div>
        </div>
        <div class="content-status">
          <span><b>{{ metrics.materials.approved }}</b> 已通过</span>
          <span><b>{{ metrics.materials.pending }}</b> 待审核</span>
          <span><b>{{ metrics.materials.rejected }}</b> 已驳回</span>
        </div>
      </article>

      <article class="panel audit-panel">
        <div class="panel-head"><div><span class="panel-kicker">AUDIT TRAIL</span><h2>最近操作</h2></div><span class="live-mark"><i />持续留痕</span></div>
        <div v-if="recentAudits.length" class="audit-list">
          <div v-for="item in recentAudits" :key="item.id" class="audit-item">
            <i class="audit-node" />
            <div><strong>{{ actionLabel(item.action) }}</strong><span>{{ targetLabel(item) }}</span></div>
            <time>{{ formatTime(item.created_at) }}</time>
          </div>
        </div>
        <div v-else class="empty-state"><i class="el-icon-finished" /><span>暂无后台操作记录</span></div>
      </article>
    </section>
  </main>
</template>

<script>
import request from '../api/request'
import ElAlert from 'element-ui/lib/alert'

const emptyMetrics = () => ({
  users: { total: 0, active: 0, today: 0 },
  creators: { total: 0, verified: 0, pending: 0 },
  demands: { total: 0, pending: 0, approved: 0, published: 0 },
  materials: { total: 0, pending: 0, approved: 0, rejected: 0, portfolio: 0, delivery: 0, chat: 0 },
  orders: { total: 0, active: 0, completed: 0, gmv: 0 },
  messages: { total: 0 }
})

export default {
  components: { ElAlert },
  data() {
    return {
      loading: false,
      error: '',
      metrics: emptyMetrics(),
      reviewQueue: { demands: 0, materials: 0, creators: 0, realnames: 0, reports: 0 },
      trends: [],
      recentAudits: []
    }
  },
  computed: {
    totalPending() { return Object.values(this.reviewQueue).reduce((sum, value) => sum + Number(value || 0), 0) },
    metricCards() {
      return [
        { label: '注册用户', value: this.formatNumber(this.metrics.users.total), note: `今日新增 ${this.metrics.users.today} · 活跃账号 ${this.metrics.users.active}`, icon: 'el-icon-user' },
        { label: '审核婚影师', value: this.formatNumber(this.metrics.creators.verified), note: `累计入驻 ${this.metrics.creators.total} · 待审 ${this.metrics.creators.pending}`, icon: 'el-icon-video-camera' },
        { label: '公开剧情', value: this.formatNumber(this.metrics.demands.published), note: `累计提交 ${this.metrics.demands.total} · 待审 ${this.metrics.demands.pending}`, icon: 'el-icon-document' },
        { label: '项目成交额', value: this.formatMoney(this.metrics.orders.gmv), note: `进行中 ${this.metrics.orders.active} · 已完成 ${this.metrics.orders.completed}`, icon: 'el-icon-wallet' }
      ]
    },
    queueCards() {
      return [
        { key: 'demands', index: '01', label: '剧情公开审核', note: '确认内容、授权与婚礼信息', value: this.reviewQueue.demands, path: '/demands' },
        { key: 'materials', index: '02', label: '视频与媒体审核', note: '先看机器结果，再做人工判断', value: this.reviewQueue.materials, path: '/videos' },
        { key: 'creators', index: '03', label: '婚影师入驻', note: '随代表作审核同步确认', value: this.reviewQueue.creators, path: '/videos' },
        { key: 'safety', index: '04', label: '实名与举报', note: '处理身份核验和用户反馈', value: this.reviewQueue.realnames + this.reviewQueue.reports, path: '/safety' }
      ]
    },
    primaryQueue() {
      return this.queueCards.find(item => item.value > 0) || { path: '/demands', action: '查看审核记录' }
    },
    trendMax() {
      const values = this.trends.reduce((all, item) => all.concat([item.users, item.demands, item.materials]), [1])
      return Math.max(...values)
    },
    contentMix() {
      const total = Math.max(Number(this.metrics.materials.total || 0), 1)
      return [
        { label: '婚影师作品', value: this.metrics.materials.portfolio, percent: this.metrics.materials.portfolio / total * 100, color: '#8f4056' },
        { label: '订单成片', value: this.metrics.materials.delivery, percent: this.metrics.materials.delivery / total * 100, color: '#c78e72' },
        { label: '聊天媒体', value: this.metrics.materials.chat, percent: this.metrics.materials.chat / total * 100, color: '#576b61' }
      ]
    }
  },
  created() { this.load() },
  methods: {
    async load() {
      this.loading = true
      this.error = ''
      try {
        const res = await request.get('/overview')
        this.metrics = { ...emptyMetrics(), ...(res.metrics || {}) }
        this.reviewQueue = { ...this.reviewQueue, ...(res.reviewQueue || {}) }
        this.trends = res.trends || []
        this.recentAudits = res.recentAudits || []
      } catch (error) {
        this.error = '运营数据暂时无法同步，请检查后台服务连接。'
      } finally {
        this.loading = false
      }
    },
    go(path) { this.$router.push(path) },
    formatNumber(value) { return new Intl.NumberFormat('zh-CN').format(Number(value || 0)) },
    formatMoney(value) { return `¥${new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 0 }).format(Number(value || 0))}` },
    shortDay(day) { return String(day || '').slice(5).replace('-', '/') },
    barStyle(value) { return { height: `${Math.max(Number(value || 0) / this.trendMax * 100, value ? 8 : 2)}%` } },
    formatTime(value) { return value ? new Date(value).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—' },
    actionLabel(action) {
      const labels = { admin_demand_approved: '剧情审核通过', admin_demand_rejected: '剧情审核驳回', admin_portfolio_approved: '婚影师作品通过', admin_portfolio_rejected: '婚影师作品驳回', admin_delivery_video_approved: '订单成片通过', admin_delivery_video_rejected: '订单成片驳回', report_reviewed: '举报处理更新', realname_verified: '实名审核通过', realname_rejected: '实名审核驳回' }
      return labels[action] || '后台操作已记录'
    },
    targetLabel(item) { return `${item.target_type || '记录'} #${item.target_id || item.id}` }
  }
}
</script>

<style scoped>
.overview-page { padding-bottom: 48px; }
.command-card { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 28px; padding: 26px 28px; margin-bottom: 22px; color: #fffaf7; background: radial-gradient(circle at 78% 15%, rgba(199,142,114,.35), transparent 34%), #3b2930; border-radius: 22px; box-shadow: 0 20px 44px rgba(58,38,46,.16); }
.command-copy { display: grid; grid-template-columns: auto auto; align-items: end; column-gap: 10px; min-width: 210px; }
.command-copy strong { font-family: Georgia, serif; font-size: 48px; line-height: .95; font-variant-numeric: tabular-nums; }
.command-copy > span:last-child { color: #d9cbc8; font-size: 13px; }
.command-label { grid-column: 1 / -1; margin-bottom: 9px; color: #d9b7af; font-size: 12px; letter-spacing: .16em; }
.command-note { color: #d9cbc8; line-height: 1.6; }
.metric-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-bottom: 18px; }
.metric-card { min-height: 132px; padding: 20px; background: rgba(255,255,255,.86); border: 1px solid rgba(91,66,71,.08); border-radius: 18px; box-shadow: 0 10px 26px rgba(71,51,57,.05); }
.metric-top { display: flex; align-items: center; justify-content: space-between; color: #786c69; font-size: 13px; }
.metric-top i { display: grid; place-items: center; width: 30px; height: 30px; color: #8f4056; background: #f5e8e9; border-radius: 9px; }
.metric-card strong { display: block; margin-top: 14px; color: #302527; font-family: Georgia, serif; font-size: 31px; font-variant-numeric: tabular-nums; }
.metric-card p { margin: 7px 0 0; color: #8d827f; font-size: 12px; }
.overview-grid { display: grid; grid-template-columns: minmax(0, 1.65fr) minmax(300px, .85fr); gap: 18px; margin-top: 18px; }
.lower-grid { grid-template-columns: minmax(320px, .9fr) minmax(0, 1.6fr); }
.panel { min-width: 0; padding: 24px; background: rgba(255,255,255,.84); border: 1px solid rgba(91,66,71,.08); border-radius: 20px; box-shadow: 0 12px 32px rgba(71,51,57,.05); }
.panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; }
.panel-head h2 { margin: 4px 0 0; color: #34292b; font-family: Georgia, 'Noto Serif SC', serif; font-size: 21px; }
.panel-kicker { color: #9d6976; font-size: 10px; font-weight: 700; letter-spacing: .16em; }
.legend { display: flex; gap: 14px; color: #807572; font-size: 12px; }
.legend span { display: flex; align-items: center; gap: 5px; }
.dot { width: 7px; height: 7px; border-radius: 50%; }
.users { background: #8f4056; }.demands { background: #c78e72; }.media { background: #576b61; }
.chart { display: grid; grid-template-columns: repeat(7, 1fr); gap: 12px; height: 210px; margin-top: 24px; padding-top: 10px; border-bottom: 1px solid #ded6d2; background: repeating-linear-gradient(to bottom, #eee8e5 0, #eee8e5 1px, transparent 1px, transparent 52px); }
.chart-day { display: grid; grid-template-rows: 1fr auto; gap: 9px; min-width: 0; color: #998e8b; font-size: 10px; text-align: center; }
.bars { display: flex; align-items: flex-end; justify-content: center; gap: 4px; height: 100%; }
.bar { width: 8px; min-height: 2px; border-radius: 5px 5px 1px 1px; transition: height .4s ease; }
.queue-panel { padding-bottom: 14px; }
.queue-row { width: 100%; display: grid; grid-template-columns: 30px 1fr auto 16px; align-items: center; gap: 10px; padding: 15px 0; color: #3c3133; text-align: left; background: none; border: 0; border-bottom: 1px solid #eee7e3; cursor: pointer; transition: transform .2s ease, color .2s ease; }
.queue-row:hover { color: #8f4056; transform: translateX(3px); }
.queue-index { color: #ac9d98; font-family: Georgia, serif; font-size: 12px; }
.queue-copy { display: grid; gap: 3px; }.queue-copy strong { font-size: 14px; }.queue-copy small { color: #958985; font-size: 11px; }
.queue-row b { min-width: 30px; padding: 5px 8px; color: #fff; text-align: center; background: #8f4056; border-radius: 9px; font-variant-numeric: tabular-nums; }.queue-row b.empty { color: #7c716e; background: #eee9e6; }
.content-total { display: flex; align-items: baseline; gap: 9px; margin: 20px 0; }.content-total strong { font-family: Georgia, serif; font-size: 34px; }.content-total span { color: #8b807d; font-size: 12px; }
.content-bars { display: grid; gap: 15px; }.content-line > div:first-child { display: flex; justify-content: space-between; margin-bottom: 7px; font-size: 12px; }.content-line b { font-variant-numeric: tabular-nums; }
.progress-track { height: 7px; overflow: hidden; background: #eee9e6; border-radius: 9px; }.progress-track i { display: block; min-width: 2px; height: 100%; border-radius: inherit; }
.content-status { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 22px; padding-top: 16px; border-top: 1px solid #eee7e3; }.content-status span { display: grid; gap: 3px; color: #8f8581; font-size: 11px; }.content-status b { color: #413638; font-size: 17px; }
.live-mark { display: inline-flex; align-items: center; gap: 6px; color: #706663; font-size: 11px; }.live-mark i { width: 7px; height: 7px; background: #4f7a63; border-radius: 50%; box-shadow: 0 0 0 4px rgba(79,122,99,.12); }
.audit-list { margin-top: 18px; }.audit-item { position: relative; display: grid; grid-template-columns: 14px 1fr auto; gap: 10px; align-items: center; padding: 11px 0; border-bottom: 1px solid #eee7e3; }.audit-node { width: 7px; height: 7px; background: #c58d79; border-radius: 50%; }.audit-item div { display: grid; gap: 3px; }.audit-item strong { font-size: 13px; }.audit-item span,.audit-item time { color: #978b87; font-size: 11px; }.empty-state { display: grid; place-items: center; gap: 10px; min-height: 180px; color: #9d918d; }
@media (max-width: 1100px) { .metric-grid { grid-template-columns: repeat(2, 1fr); }.overview-grid,.lower-grid { grid-template-columns: 1fr; } }
@media (max-width: 720px) { .command-card { grid-template-columns: 1fr; }.command-note { display: none; }.metric-grid { grid-template-columns: 1fr; }.legend { display: none; } }
</style>
